/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { allStoresListSX } from '@/components/content/StoreLocator/styles/SideList/element';
import { storeLocatorSideListSideListSX } from '@/components/content/StoreLocator/styles/SideList/sideList';
import '@/components/content/StoreLocator/styles/SideList/side-list.scss';
import { useLocalization } from '@/data/Localization';
import { Box, List, ListItem, Stack, Typography } from '@mui/material';
import { FC, MouseEventHandler, useContext, useMemo } from 'react';
import { ContentContext } from '@/data/context/content';
import { useStoreLocatorPage } from '@/data/Content/StoreLocatorPage';
import { useNextRouter } from '@/data/Content/_NextRouter';

export const StoreLocatorSideList: FC = () => {
	const localization = useLocalization('StoreLocator');
	const router = useNextRouter();
	const { storesByCity, cityURLs } = useContext(ContentContext) as ReturnType<
		typeof useStoreLocatorPage
	>;

	const cityStores = useMemo(
		() => (storesByCity ? Object.entries(storesByCity) : []).filter(([, stores]) => stores.length),
		[storesByCity]
	);

	// The linkable component is heavy when used in big quantities, to prevent rendering hundreds of
	// these, a single catch all is used that triggers NextJS routing on click on a link
	const onClickCatcher: MouseEventHandler<HTMLDivElement> = (event) => {
		if (
			!event.nativeEvent?.target ||
			!('tagName' in event.nativeEvent?.target) ||
			(event.nativeEvent?.target.tagName || '').toString().toLowerCase() !== 'a' ||
			!('href' in event.nativeEvent?.target)
		) {
			return;
		}

		event.preventDefault();
		router.push(event.nativeEvent.target.href as string);
	};

	return (
		<Stack sx={storeLocatorSideListSideListSX}>
			<List sx={{ maxHeight: '600px' }} disablePadding>
				<ListItem>
					<Typography sx={allStoresListSX}>{localization.allIntertoysStore.t()}</Typography>
				</ListItem>
				<ListItem>
					<Box pb={24} pt={0} onClick={onClickCatcher}>
						{cityStores.map(([cityName, stores]) => (
							// eslint-disable-next-line react/forbid-elements
							<div key={cityName} className="storeLocatorSideList__city">
								{cityURLs && cityURLs[cityName] ? (
									<a href={cityURLs[cityName]} className="storeLocatorSideList__cityName">
										{cityName}
									</a>
								) : (
									// eslint-disable-next-line react/forbid-elements
									<div className="storeLocatorSideList__cityName">{cityName}</div>
								)}
								{stores.map((store, index) =>
									store.storePageURL ? (
										<a
											href={store.storePageURL}
											key={index}
											className="storeLocatorSideList__store"
										>
											{store.storeName}
											<br />
											{store.storeFullAddress}
											<br />
											{store.postalCode} {store.city}
										</a>
									) : (
										// eslint-disable-next-line react/forbid-elements
										<div key={index} className="storeLocatorSideList__store">
											{store.storeName}
											<br />
											{store.storeFullAddress}
											<br />
											{store.postalCode} {store.city}
										</div>
									)
								)}
							</div>
						))}
					</Box>
				</ListItem>
			</List>
		</Stack>
	);
};
